import { useCallback, useMemo, memo } from 'react';
import { Droplet, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TimelineItem } from '@/types/timeline';
import type { TransformProperties, CanvasSettings } from '@/types/transform';
import { useGizmoStore } from '@/features/preview/stores/gizmo-store';
import { useThrottledFrame } from '@/features/preview/hooks/use-throttled-frame';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import {
  resolveTransform,
  getSourceDimensions,
} from '@/lib/composition-runtime/utils/transform-resolver';
import { resolveAnimatedTransform } from '@/features/keyframes/utils/animated-transform-resolver';
import { autoKeyframeProperty } from '@/features/keyframes/utils/auto-keyframe';
import {
  PropertySection,
  PropertyRow,
  NumberInput,
} from '../components';
import { KeyframeToggle } from '@/features/keyframes/components/keyframe-toggle';
import { useTranslation } from '@/features/i18n/locales';

interface FillSectionProps {
  items: TimelineItem[];
  canvas: CanvasSettings;
  onTransformChange: (ids: string[], updates: Partial<TransformProperties>) => void;
}

type MixedValue = number | 'mixed';

/**
 * Fill section - opacity and corner radius.
 */
export const FillSection = memo(function FillSection({
  items,
  canvas,
  onTransformChange,
}: FillSectionProps) {
  const t = useTranslation();
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  const currentFrameVal = useThrottledFrame();
  const allKeyframesVal = useTimelineStore((s) => s.keyframes);
  const setTransformPreviewVal = useGizmoStore((s) => s.setTransformPreview);
  const clearPreviewVal = useGizmoStore((s) => s.clearPreview);

  const { opacityRaw, cornerRadius } = useMemo(() => {
    if (items.length === 0) {
      return { opacityRaw: 1 as MixedValue, cornerRadius: 0 as MixedValue };
    }

    const resolvedValues = items.map((item) => {
      const sourceDimensions = getSourceDimensions(item);
      const baseResolved = resolveTransform(item, canvas, sourceDimensions);

      // Apply keyframe animation if item has keyframes
      const itemKeyframes = allKeyframesVal.find((k: any) => k.itemId === item.id);
      if (itemKeyframes) {
        const relativeFrame = currentFrameVal - item.from;
        return resolveAnimatedTransform(baseResolved, itemKeyframes, relativeFrame);
      }

      return baseResolved;
    });

    const getVal = (getter: (r: ReturnType<typeof resolveTransform>) => number): MixedValue => {
      const values = resolvedValues.map(getter);
      const firstValue = values[0]!;
      return values.every((v) => Math.abs(v - firstValue) < 0.01) ? firstValue : 'mixed';
    };

    return {
      opacityRaw: getVal((r) => r.opacity),
      cornerRadius: getVal((r) => r.cornerRadius),
    };
  }, [items, canvas, allKeyframesVal, currentFrameVal]);

  const opacity = opacityRaw === 'mixed' ? 'mixed' : Math.round(opacityRaw * 100);

  const addKeyframe = useTimelineStore((s) => s.addKeyframe);
  const updateKeyframe = useTimelineStore((s) => s.updateKeyframe);

  const autoKeyframeOpacity = useCallback(
    (itemId: string, value: number): boolean => {
      const item = items.find((i) => i.id === itemId);
      if (!item) return false;
      const itemKeyframes = allKeyframesVal.find((k: any) => k.itemId === itemId);
      return autoKeyframeProperty(item, itemKeyframes, 'opacity', value, currentFrameVal, addKeyframe, updateKeyframe);
    },
    [items, allKeyframesVal, currentFrameVal, addKeyframe, updateKeyframe]
  );

  const autoKeyframeCornerRadius = useCallback(
    (itemId: string, value: number): boolean => {
      const item = items.find((i) => i.id === itemId);
      if (!item) return false;
      const itemKeyframes = allKeyframesVal.find((k: any) => k.itemId === itemId);
      return autoKeyframeProperty(item, itemKeyframes, 'cornerRadius', value, currentFrameVal, addKeyframe, updateKeyframe);
    },
    [items, allKeyframesVal, currentFrameVal, addKeyframe, updateKeyframe]
  );

  const handleOpacityLiveChange = useCallback((value: number) => {
    const previews: Record<string, { opacity: number }> = {};
    items.forEach((item) => { previews[item.id] = { opacity: value / 100 }; });
    setTransformPreviewVal(previews);
  }, [items, setTransformPreviewVal]);

  const handleOpacityChange = useCallback((value: number) => {
    const opacityValue = value / 100;
    let allHandled = true;
    for (const itemId of itemIds) { if (!autoKeyframeOpacity(itemId, opacityValue)) allHandled = false; }
    if (!allHandled) onTransformChange(itemIds, { opacity: opacityValue });
    queueMicrotask(() => clearPreviewVal());
  }, [itemIds, onTransformChange, clearPreviewVal, autoKeyframeOpacity]);

  const handleCornerRadiusLiveChange = useCallback((value: number) => {
    const previews: Record<string, { cornerRadius: number }> = {};
    items.forEach((item) => { previews[item.id] = { cornerRadius: value }; });
    setTransformPreviewVal(previews);
  }, [items, setTransformPreviewVal]);

  const handleCornerRadiusChange = useCallback((value: number) => {
    let allHandled = true;
    for (const itemId of itemIds) { if (!autoKeyframeCornerRadius(itemId, value)) allHandled = false; }
    if (!allHandled) onTransformChange(itemIds, { cornerRadius: value });
    queueMicrotask(() => clearPreviewVal());
  }, [itemIds, onTransformChange, clearPreviewVal, autoKeyframeCornerRadius]);

  const handleResetOpacity = useCallback(() => {
    const tolerance = 0.01;
    const needsUpdate = items.some((item) => {
      const sourceDimensions = getSourceDimensions(item);
      const resolved = resolveTransform(item, canvas, sourceDimensions);
      return Math.abs(resolved.opacity - 1) > tolerance;
    });
    if (needsUpdate) onTransformChange(itemIds, { opacity: 1 });
  }, [items, itemIds, onTransformChange, canvas]);

  const handleResetCornerRadius = useCallback(() => {
    const tolerance = 0.5;
    const needsUpdate = items.some((item) => {
      const sourceDimensions = getSourceDimensions(item);
      const resolved = resolveTransform(item, canvas, sourceDimensions);
      return resolved.cornerRadius > tolerance;
    });
    if (needsUpdate) onTransformChange(itemIds, { cornerRadius: 0 });
  }, [items, itemIds, onTransformChange, canvas]);

  return (
    <PropertySection title={t.properties.fill} icon={Droplet} defaultOpen={true}>
      <PropertyRow label={t.properties.opacity}>
        <div className="flex items-center gap-1 w-full">
          <KeyframeToggle itemIds={itemIds} property="opacity" currentValue={opacityRaw === 'mixed' ? 1 : opacityRaw} />
          <NumberInput value={opacity} onChange={handleOpacityChange} onLiveChange={handleOpacityLiveChange} min={0} max={100} step={1} unit="%" className="flex-1 min-w-0" />
          <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={handleResetOpacity} title={t.properties.resetOpacity}>
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>
      <PropertyRow label={t.properties.radius}>
        <div className="flex items-center gap-1 w-full">
          <KeyframeToggle itemIds={itemIds} property="cornerRadius" currentValue={cornerRadius === 'mixed' ? 0 : cornerRadius} />
          <NumberInput value={cornerRadius} onChange={handleCornerRadiusChange} onLiveChange={handleCornerRadiusLiveChange} min={0} max={1000} step={1} unit="px" className="flex-1 min-w-0" />
          <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={handleResetCornerRadius} title={t.properties.resetRadius}>
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>
    </PropertySection>
  );
});
